import { useCallback, useEffect, useId, useRef, useState } from "react";
import { api } from "@/comn";
import { Icon, Badge } from "@/comn/components";
import { SurvPrtcpntPpupOga } from "@/comn/comn/surv/SurvPrtcpntPpupOga";

import { useModal } from "@/comn/hooks";
import { SurvPrtcpntPpupInt } from "@/comn/comn/surv/SurvPrtcpntPpupInt";
import { SurvPrtcpntPpupExt } from "@/comn/comn/surv/SurvPrtcpntPpupExt";

export const HeaderSurvButton = () => {
    const id = useId();
    const handleClick = () => {};
    const [data, setData] = useState([]);
    const [count, setCount] = useState(0);
    const [qestnrId, setQestnrId] = useState("");
    const [frstTime, setFrstTime] = useState(false);
    const modal = useModal();

    const openedRef = useRef(false);
    // 세션 유저 읽기
    const readSessionUser = () => {
        try {
            const raw = sessionStorage.getItem("user-info") || localStorage.getItem("user-info");
            return raw ? JSON.parse(raw) : {};
        } catch {
            return {};
        }
    };

    const hasSessionUser = () => {
        // 세션 로그인 유무 판단
        return !!sessionStorage.getItem("user-info");
    };

    const getSurvCount = useCallback(async (target: any) => {
        try {
            let base = "",
                path = "";
            switch (process.env.REACT_APP_SYSTEM_GROUP) {
                case "tancis":
                    base = process.env.REACT_APP_API_PTLI || "";
                    path = "/api/v1/ptli/intptl/mypage/surv/ppup";
                    break;
                case "tanesw":
                    base = process.env.REACT_APP_API_PTLE || "";
                    path = "/api/v1/ptle/extptl/mypage/surv/ppup";
                    break;
                case "tanoga":
                    base = process.env.REACT_APP_API_PTLO || "";
                    path = "/api/v1/eswo/ogaptl/mypage/surv/ppup";
                    break;
            }
            const usr: any = readSessionUser();
            let params: Record<string, any> = {
                page: 0,
                size: 1,
                bsopClsfCd: "PTL",
            };
            switch (process.env.REACT_APP_SYSTEM_GROUP) {
                case "tancis":
                    params.usrDmnId = usr?.usrId ?? usr?.asisSysmUsrId ?? "";
                    params.qustnrTrgtAcrdtYn = target;
                    break;
                case "tanesw":
                case "tanoga":
                    params.xtrnUsrUuid = usr?.xtrnUsrUuid;
                    params.usrTin = usr?.usrId ?? usr?.asisSysmUsrId;
                    params.qustnrTrgtAcrdtYn = target;
                    break;
            }
            const res = await api.get(base + path, { params });
            const list = res?.data?.survPrtcpntList?.content ?? [];
            const total = res?.data?.survPrtcpntList?.page?.totalElements ?? 0;
            const firstId = list.length > 0 ? list[0]?.qestnrId ?? null : null;
            setQestnrId(firstId);
            setCount(total > 0 ? 1 : 0);
        } catch (e) {
            setCount(0);
        }
    }, []);

    type LangCd = "KO" | "EN" | "TZ";

    type SurveyCompProps = { qestnrId: string; langCd: LangCd; onAfterSubmit: () => void };

    const pickSurveyComponent = (): React.ComponentType<SurveyCompProps> => {
        switch (process.env.REACT_APP_SYSTEM_GROUP) {
            case "tancis":
                return SurvPrtcpntPpupInt;
            case "tanesw":
                return SurvPrtcpntPpupExt;
            case "tanoga":
                return SurvPrtcpntPpupOga;
            default:
                return SurvPrtcpntPpupInt;
        }
    };

    const readSessionLangCd = (): LangCd => {
        try {
            const raw = sessionStorage.getItem("menu-config") || localStorage.getItem("menu-config");
            if (!raw) return "EN"; // 메뉴 설정이 없으면 EN 기본값
            const parsed = JSON.parse(raw);

            const cd = parsed?.langCd;
            if (cd === "KO" || cd === "EN" || cd === "TZ") {
                return cd;
            }
            return "EN";
        } catch {
            return "EN";
        }
    };
    const openSurveyPopup = useCallback(
        (qid: string, langCd?: LangCd) => {
            const SurveyComp = pickSurveyComponent();

            // langCd가 넘어오면 그 값 사용, 아니면 기존처럼 세션에서 읽기
            const resolvedLangCd: LangCd = langCd ?? readSessionLangCd();

            modal.openModal({
                size: "lg",
                draggable: true,
                close: false,
                topClose: false,
                content: (
                    <SurveyComp
                        qestnrId={qid}
                        langCd={resolvedLangCd}
                        onAfterSubmit={() => {
                            modal.closeModal();
                        }}
                    />
                ),
            });
        },
        [modal],
    );

    useEffect(() => {
        // 최초 진입: 세션에 user-info가 없으면 non-user(2), 있으면 user(1)
        const target = hasSessionUser() ? "1" : "2";
        getSurvCount(target);

        // 다른 탭/윈도우에서 로그인되어 user-info가 생기는 경우 대응
        const onStorage = (e: StorageEvent) => {
            if (e.key === "user-info") {
                const nowLoggedIn = !!sessionStorage.getItem("user-info"); // 같은 탭은 sessionStorage로 판별
                getSurvCount(nowLoggedIn ? "1" : "2");
            }
        };
        window.addEventListener("storage", onStorage);

        // 같은 탭 내에서도 페이지 가렸다 다시 올릴 때 상태 변화 반영(보수적으로 체크)
        const onVisibility = () => {
            const target = hasSessionUser() ? "1" : "2";
            getSurvCount(target);
        };
        document.addEventListener("visibilitychange", onVisibility);

        return () => {
            window.removeEventListener("storage", onStorage);
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, [getSurvCount]);

    useEffect(() => {
        if (openedRef.current) return;
        if (!qestnrId || count <= 0) return;

        const storageKey = `survey-ppupCheck`;

        if (sessionStorage.getItem(storageKey) === "1") {
            openedRef.current = true;
            return;
        }

        openedRef.current = true;
        sessionStorage.setItem(storageKey, "1");

        // 🔥 여기서 처음 자동 오픈은 langCd를 무조건 'EN'으로 지정
        openSurveyPopup(qestnrId, "EN");
    }, [qestnrId, count, openSurveyPopup]);

    return (
        <div className="group relative">
            <Badge number={count}>
                <button
                    type="button"
                    className="flex items-center justify-center bg-header rounded h-[32px] w-[32px] hover:bg-black/10 transition-colors"
                >
                    <Icon
                        icon="calendar"
                        className="text-text"
                        onClick={() => count > 0 && openSurveyPopup(qestnrId)}
                    />
                </button>
            </Badge>
        </div>
    );
};
